/**
 * The Subskrypcja tab: what the trainer has, and what they may change it to.
 *
 * Owner-approved polish of 4 November 2026: „Twój plan" first, as a group of rows (the plan, its
 * active clients, the renewal, where it was bought, and where it is managed), then „Zmień plan" —
 * the catalogue (`PlanPicker`). Requirement R13 makes this the one web surface for subscriptions:
 * on the free plan the cards lead to the payment page, on a web subscription to the provider's
 * portal, and on a store subscription to the store, because the channel that sold a subscription is
 * the only one that manages it (R4).
 *
 * Every decision is `model/subscriptionCard`'s and `model/webOffers`': which sentence, which count,
 * whether a row exists, whether a bar is drawn. This file lays them out.
 *
 * ── The catalogue waits for the plan above it ───────────────────────────────────────────────
 * Which rows „Twój plan" has is the entitlement's answer — a free trainer has two, a web subscriber
 * six — so the group cannot reserve its loaded height before the read lands. It reserves the
 * SHORTER shape instead (its two rows that every plan has), and nothing is drawn under it until the
 * answer is in: the rows that arrive then grow a box with nothing below it, and the catalogue
 * appears under the finished group rather than being pushed down by it.
 *
 * ── The bar is decoration, and the legend is not ────────────────────────────────────────────
 * The segments are `aria-hidden`: every number they encode is already written in words beside them.
 * What a listener gets instead is the figure, the caption and `seatsA11y`.
 */

import { useTranslation } from 'react-i18next';

import { FormBanner } from '~/shared/ui/FormBanner';
import inlineText from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SeatSegment } from '~/entities/client';

import type { AccountSubscriptionApi } from '../lib/useSubscriptionCard';
import type { WebCheckoutApi } from '../lib/useWebCheckout';
import { SECONDARY_PILL } from './accountButtons';
import { ExternalIcon, MailIcon } from './AccountIcons';
import { PlanPicker } from './PlanPicker';
import screen from './AccountScreen.module.css';
import styles from './SubscriptionTab.module.css';

interface SubscriptionTabProps {
  readonly subscription: AccountSubscriptionApi;
  readonly checkout: WebCheckoutApi;
}

/** A seat's treatment, written once: the bar's cells and the legend's marks wear the same three. */
const SEAT_CLASS: Readonly<Record<SeatSegment, string>> = {
  active: String(styles.seatActive),
  pending: String(styles.seatPending),
  free: '',
};

export function SubscriptionTab({ subscription, checkout }: SubscriptionTabProps) {
  const { t } = useTranslation();
  const { card, state, waiting, skeleton, reveal } = subscription;
  const failed = !waiting && state === 'error';
  // The one subscriber line the notice row may hold, by priority (see the row's comment).
  const subscriberLine = card?.parallelBilling ?? card?.pendingChange ?? null;

  return (
    <div className={screen.inner}>
      {/* The failure is content too: it waits for the bars' minimum life like the rows do, so the
        * banner does not stand over a group still drawing its skeleton. */}
      {failed && (
        <div className={styles.failure}>
          <FormBanner message={t('web:account.subscription.loadFailed')} />
          <button type="button" className={SECONDARY_PILL} onClick={subscription.onRetry}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!failed && (
        <>
          <h2 className={screen.groupLabel}>{t('web:account.subscription.planGroup')}</h2>
          {/* `aria-busy` and the hidden sentence follow the WAIT, like the bars inside the group. */}
          <div className={screen.group} aria-busy={waiting}>
            {waiting && <span className={hidden.srOnly}>{t('web:account.subscription.waiting')}</span>}

            <div className={screen.groupRow}>
              <span className={screen.rowLabel}>{t('web:account.subscription.planLabel')}</span>
              <span className={screen.rowBody}>
                {skeleton && <span className={`${String(skel.bar)} ${String(styles.valueBar)}`} />}
                {card !== null && <span className={`${String(styles.value)} ${reveal}`}>{card.planLine}</span>}
              </span>
            </div>

            <div className={screen.groupRow}>
              <span className={screen.rowLabel}>{t('web:account.subscription.seatsLabel')}</span>
              <div className={screen.rowBody}>
                {skeleton && <span className={`${String(skel.bar)} ${String(styles.valueBar)}`} />}
                {card !== null && (
                  <div className={reveal}>
                    <p className={styles.figureLine}>
                      <b className={styles.figure}>{card.seatFigure}</b>
                      <span>{card.seatCaption}</span>
                    </p>
                    {/* A capped plan's seats, cell by cell, and the legend that names the cells. */}
                    {card.segments.length > 0 && (
                      <>
                        <span className={styles.bar} aria-hidden="true">
                          {card.segments.map((segment, index) => (
                            <i key={index} className={`${String(styles.seat)} ${SEAT_CLASS[segment]}`} />
                          ))}
                        </span>
                        <p className={styles.legend}>
                          {card.legend.map((entry) => (
                            <span key={entry.kind} className={styles.legendEntry}>
                              <i
                                className={`${String(styles.legendMark)} ${SEAT_CLASS[entry.kind]}`}
                                aria-hidden="true"
                              />
                              {entry.label}
                            </span>
                          ))}
                        </p>
                      </>
                    )}
                    {/* The seats as one sentence — what the cells would otherwise say in colour. */}
                    <span className={hidden.srOnly}>{card.seatsA11y}</span>
                  </div>
                )}
              </div>
            </div>

            {/* The renewal — or, during a web trial (R19), the trial's end and what follows it. A
              * plan with no renewal date (the free plan, a grant without an end) has no row: the
              * value is absent, so the row is too, rather than a dash standing for it. */}
            {card?.renewal != null && (
              <div className={`${String(screen.groupRow)} ${reveal}`}>
                <span className={screen.rowLabel}>{card.renewalTitle}</span>
                <span className={screen.rowBody}>
                  <span className={styles.value}>{card.renewal}</span>
                  {card.renewalNote !== null && <span className={styles.note}>{card.renewalNote}</span>}
                </span>
              </div>
            )}

            {card?.boughtIn != null && (
              <div className={`${String(screen.groupRow)} ${reveal}`}>
                <span className={screen.rowLabel}>{t('web:account.subscription.boughtIn')}</span>
                <span className={`${String(screen.rowBody)} ${String(styles.value)}`}>{card.boughtIn}</span>
              </div>
            )}

            {/* Only when a storefront genuinely manages this plan. A comped subscription has
              * nowhere to send anybody, and a link into the store's list reads as broken. */}
            {card?.manage === 'store' && card.store !== null && (
              <div className={`${String(screen.groupRow)} ${reveal}`}>
                <span className={screen.rowLabel}>{t('web:account.subscription.manage')}</span>
                <span className={screen.rowBody}>
                  {t('web:account.subscription.manageNote', { store: card.store.name })}
                </span>
                <div className={screen.rowActions}>
                  <a
                    className={SECONDARY_PILL}
                    href={card.store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('web:account.app.help.opensExternally', {
                      label: t('web:account.subscription.manage'),
                    })}
                  >
                    <ExternalIcon className={screen.pillGlyph} />
                    {card.store.name}
                  </a>
                </div>
              </div>
            )}

            {/* The web's own two rows, for a subscription bought here. The portal does every change
              * R8 allows, and the receipts live in it. */}
            {card?.manage === 'portal' && (
              <>
                <div className={`${String(screen.groupRow)} ${reveal}`}>
                  <span className={screen.rowLabel}>{t('web:account.subscription.manage')}</span>
                  <span className={screen.rowBody}>{t('web:account.subscription.portalNote')}</span>
                  <div className={screen.rowActions}>
                    <button
                      type="button"
                      className={SECONDARY_PILL}
                      aria-disabled={checkout.portalBusy}
                      onClick={() => {
                        if (checkout.portalBusy) return;
                        checkout.onOpenPortal();
                      }}
                    >
                      <ExternalIcon className={screen.pillGlyph} />
                      {t('web:account.subscription.portalAction')}
                    </button>
                  </div>
                </div>

                {/* By hand at launch (R15). The subject is prefilled and nothing else is: an
                  * account's own address in a `mailto` query string is personal data in a URL. */}
                <div className={`${String(screen.groupRow)} ${reveal}`}>
                  <span className={screen.rowLabel}>{t('web:account.subscription.invoiceTitle')}</span>
                  <span className={screen.rowBody}>{t('web:account.subscription.invoiceNote')}</span>
                  <span className={screen.rowActions}>
                    <MailIcon className={screen.rowIcon} />
                    <a
                      className={styles.mail}
                      href={`mailto:${t('web:account.subscription.invoiceAddress')}?subject=${encodeURIComponent(
                        t('web:account.subscription.invoiceSubject'),
                      )}`}
                    >
                      {t('web:account.subscription.invoiceAddress')}
                    </a>
                  </span>
                </div>
              </>
            )}
          </div>
          <FormBanner message={checkout.portalError} />
        </>
      )}

      {/* Everything below waits for the plan above it — see the header. */}
      {!waiting && (card !== null || failed) && (
        <>
          {/* ── ONE line of room, where three sentences can stand ──────────────────────────────
            * The wait after a payment, the double-billing warning (R3) and the queued change are
            * the same kind of line, one at a time, by priority. The room is held empty when there
            * is nothing to say, so a sentence arriving in it moves nothing under it. */}
          {card !== null && (
            <div className={styles.noticeLine}>
              {checkout.notice !== null ? (
                <p className={styles.notice}>
                  {checkout.notice}
                  {/* One press re-arms the same wait, with the same cap. */}
                  {checkout.onRetryActivation !== null && (
                    <button
                      type="button"
                      className={`${String(inlineText.inlineTextButton)} ${String(styles.noticeRetry)}`}
                      onClick={checkout.onRetryActivation}
                    >
                      {t('web:account.subscription.activatingRetry')}
                    </button>
                  )}
                </p>
              ) : (
                checkout.face === 'rows' &&
                subscriberLine !== null && <p className={styles.notice}>{subscriberLine}</p>
              )}
            </div>
          )}

          {/* The cards stand for EVERY trainer with an answered entitlement, subscriber included:
            * what a card's press does is the channel's (`checkout.route`). */}
          {checkout.route !== null && <PlanPicker checkout={checkout} />}
        </>
      )}
    </div>
  );
}
